import { z } from 'zod';
// ── Funnel (§7.1) ─────────────────────────────────────────────────────────────
export const FUNNEL_STAGES = [
    'identified',
    'contacted',
    'engaged',
    'discovery',
    'requirement',
    'commercials',
    'won',
];
export const EXIT_STAGES = ['nurture', 'lost', 'disqualified'];
export const ALL_STAGES = [...FUNNEL_STAGES, ...EXIT_STAGES];
export function isFunnelStage(s) {
    return FUNNEL_STAGES.includes(s);
}
export function isExitStage(s) {
    return EXIT_STAGES.includes(s);
}
export const STAGE_INFO = {
    identified: {
        label: 'Identified',
        meaning: 'ICP-matched account with a named decision-maker and a noted trigger',
        entersWhen: 'Account and contact added with ICP tag',
        leavesWhen: 'First touch sent',
        checklist: ['ICP', 'Trigger', 'Persona', 'Source'],
    },
    contacted: {
        label: 'Contacted',
        meaning: 'Outreach sent, no two-way contact yet',
        entersWhen: 'First touch logged',
        leavesWhen: 'They reply, or the cadence ends',
        checklist: ['Channel', 'Pitch'],
    },
    engaged: {
        label: 'Engaged',
        meaning: 'A real two-way interaction: a reply, a call picked up, or a question asked',
        entersWhen: 'Any reply that is not a rejection',
        leavesWhen: 'A discovery call is booked',
        checklist: ['Reply outcome (positive or neutral)'],
    },
    discovery: {
        label: 'Discovery',
        meaning: 'A call or meeting is booked or held to understand hiring needs',
        entersWhen: 'Meeting scheduled',
        leavesWhen: 'Need confirmed or ruled out',
        checklist: ['Hiring pain', 'Volume', 'Urgency', 'Current vendors', 'Decision-maker', 'Service that fits'],
    },
    requirement: {
        label: 'Requirement',
        meaning: 'A live requirement is confirmed: roles, headcount, urgency, ideally a JD',
        entersWhen: 'Need confirmed with at least one role',
        leavesWhen: 'Rates and terms shared',
        checklist: ['Roles', 'Headcount', 'JD received (yes/no)', 'Potential revenue'],
    },
    commercials: {
        label: 'Commercials',
        meaning: 'Rates, commercial model and terms under discussion, including MSA or empanelment',
        entersWhen: 'Rates or proposal sent',
        leavesWhen: 'Terms accepted in writing',
        checklist: ['Commercial model', 'MSA / empanelment status'],
    },
    won: {
        label: 'Won',
        meaning: 'Terms agreed in writing; cleared to submit profiles',
        entersWhen: 'Written acceptance (email, MSA, signed rate card or PO)',
        leavesWhen: 'First placement is recorded as a milestone',
        checklist: ['Agreement evidence', 'Agreement date', 'Potential revenue'],
    },
    nurture: {
        label: 'Nurture',
        meaning: 'Not now, or no current need. Returns when a new hiring signal appears.',
        entersWhen: 'Prospect says not now',
        leavesWhen: 'Re-engage date reached or new signal',
        checklist: ['Re-engage date'],
    },
    lost: {
        label: 'Lost',
        meaning: 'Opportunity lost after engagement',
        entersWhen: 'Deal will not proceed',
        leavesWhen: '—',
        checklist: ['Lost reason'],
    },
    disqualified: {
        label: 'Disqualified',
        meaning: 'Not a fit for the ICP; kept out of conversion numbers',
        entersWhen: 'Found not to fit',
        leavesWhen: '—',
        checklist: ['Disqualification reason'],
    },
};
const text = (max = 500) => z.string().trim().min(1, 'Required').max(max);
const isoDate = z.iso.date();
export const URGENCY_LEVELS = ['low', 'medium', 'high'];
export const COMMERCIAL_MODELS = ['permanent_fee', 'contract_rate', 'both'];
export const MSA_STATUSES = ['not_started', 'in_progress', 'signed', 'empanelled'];
export const AGREEMENT_TYPES = ['written_confirmation', 'msa', 'signed_rate_card', 'purchase_order'];
/**
 * Minimum data to ENTER each stage (§7.2, M3-05). The server validates the target stage's schema
 * against the opportunity's accumulated stage data merged with what the move supplies; requirement
 * and won additionally need potential revenue (M3-08, M16-06).
 */
export const stageEntrySchemas = {
    identified: z.object({ icpId: z.uuid(), trigger: text(1000), persona: text(120), source: text(120) }),
    contacted: z.object({ channel: text(60), pitchId: z.uuid() }),
    engaged: z.object({ replyOutcome: z.enum(['positive', 'neutral']) }),
    discovery: z.object({
        hiringPain: text(1000),
        volume: text(200),
        urgency: z.enum(URGENCY_LEVELS),
        currentVendors: text(500),
        decisionMaker: text(200),
        serviceId: z.uuid(),
    }),
    requirement: z.object({
        roles: text(1000),
        headcount: z.coerce.number().int().min(1).max(10_000),
        jdReceived: z.boolean(),
    }),
    commercials: z.object({ commercialModel: z.enum(COMMERCIAL_MODELS), msaStatus: z.enum(MSA_STATUSES) }),
    won: z.object({
        agreementType: z.enum(AGREEMENT_TYPES),
        agreementDate: isoDate,
        startDate: isoDate.nullish(),
    }),
    nurture: z.object({ nurtureUntil: isoDate }),
    lost: z.object({ lostReason: text(60), lostNote: z.string().trim().max(500).nullish() }),
    disqualified: z.object({ disqualifyReason: text(60) }),
};
/** Stages whose entry also requires potential revenue on the opportunity. */
export const STAGES_REQUIRING_REVENUE = ['requirement', 'won'];
export const moveStageSchema = z.object({
    toStage: z.enum(ALL_STAGES),
    /** data for the target stage (merged into the opportunity's stage data) */
    data: z.record(z.string(), z.unknown()).default({}),
    /** required for backward moves and for leaving an exit (M3-06) */
    reason: z.string().trim().max(500).nullish(),
    /** optimistic concurrency: the version the client last saw */
    expectedVersion: z.number().int().optional(),
});
// ── Accounts & contacts ──────────────────────────────────────────────────────
export const createAccountSchema = z.object({
    name: z.string().trim().min(1).max(200),
    website: z
        .url()
        .nullish()
        .or(z.literal('').transform(() => null)),
    industry: z.string().trim().max(120).nullish(),
    headcountBand: z.string().trim().max(20).nullish(),
    location: z.string().trim().max(160).nullish(),
    funding: z.string().trim().max(160).nullish(),
    hiringSignal: z.string().trim().max(1000).nullish(),
    icpId: z.uuid().nullish(),
    source: z.string().trim().max(60).nullish(),
});
export const updateAccountSchema = createAccountSchema
    .partial()
    .refine((v) => Object.keys(v).length > 0, 'Nothing to update');
const optionalEmail = z
    .string()
    .trim()
    .toLowerCase()
    .pipe(z.email())
    .nullish()
    .or(z.literal('').transform(() => null));
const optionalPhone = z
    .string()
    .trim()
    .regex(/^\+?[0-9 ()-]{7,20}$/, 'Use digits, spaces, + ( ) -')
    .nullish()
    .or(z.literal('').transform(() => null));
const optionalLinkedin = z
    .string()
    .trim()
    .regex(/^https?:\/\/([a-z]{2,3}\.)?linkedin\.com\/.+/i, 'Use a linkedin.com profile URL')
    .nullish()
    .or(z.literal('').transform(() => null));
export const createContactSchema = z.object({
    accountId: z.uuid(),
    firstName: z.string().trim().min(1).max(80),
    lastName: z.string().trim().max(80).nullish(),
    title: z.string().trim().max(160).nullish(),
    persona: z.string().trim().max(60).nullish(),
    email: optionalEmail,
    phone: optionalPhone,
    linkedinUrl: optionalLinkedin,
    /** mandatory: where this personal data came from, and why we may use it (M18-05) */
    dataSource: z.string().trim().min(1, 'Data source is required').max(60),
    lawfulBasis: z.string().trim().min(3, 'Lawful basis is required').max(500),
});
export const updateContactSchema = createContactSchema
    .omit({ accountId: true })
    .partial()
    .refine((v) => Object.keys(v).length > 0, 'Nothing to update');
export const createOpportunitySchema = z.object({
    accountId: z.uuid(),
    contactId: z.uuid().nullish(),
    title: z.string().trim().max(200).nullish(),
    serviceId: z.uuid().nullish(),
    nextActionAt: isoDate.nullish(),
    nextActionNote: z.string().trim().max(280).nullish(),
    /** Identified entry data (§7.2) */
    icpId: z.uuid(),
    trigger: text(1000),
    persona: text(120),
    source: text(120),
});
export const updateOpportunitySchema = z
    .object({
    title: z.string().trim().max(200).nullable(),
    contactId: z.uuid().nullable(),
    serviceId: z.uuid().nullable(),
    nextActionAt: isoDate.nullable(),
    nextActionNote: z.string().trim().max(280).nullable(),
    ownerId: z.uuid(),
    firstPlacementAt: isoDate.nullable(),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, 'Nothing to update');
export const boardFiltersSchema = z.object({
    icpId: z.uuid().optional(),
    serviceId: z.uuid().optional(),
    ownerId: z.uuid().optional(),
    rag: z.enum(['green', 'amber', 'red']).optional(),
    q: z.string().trim().max(100).optional(),
    /** created on/after (YYYY-MM-DD) */
    from: isoDate.optional(),
    to: isoDate.optional(),
});
// ── Potential revenue month grid (M16-02/03) ────────────────────────────────
export const REVENUE_TYPES = ['permanent', 'staffing'];
const yearMonth = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Use YYYY-MM');
export const createRevenueLineSchema = z.object({
    type: z.enum(REVENUE_TYPES),
    /** rupees (converted to paise server-side); one-time for permanent, monthly for staffing */
    amount: z.number().positive().max(1e12),
    startMonth: yearMonth,
    /** staffing only: defaults to 12 (M16-03) */
    months: z.number().int().min(1).max(36).optional(),
});
export const setRevenueMonthSchema = z.object({ included: z.boolean() });
// ── Targets (M16-01/05) ──────────────────────────────────────────────────────
export const upsertTargetSchema = z.object({
    periodType: z.enum(['month', 'quarter']),
    /** first month of the period, YYYY-MM (quarters start Jan/Apr/Jul/Oct) */
    periodStart: yearMonth,
    closuresTarget: z.number().int().min(0).max(10_000),
    /** rupees */
    revenueTarget: z.number().min(0).max(1e13),
});
//# sourceMappingURL=pipeline.js.map